import {ChangeDetectorRef, Component, Inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoginService} from '../../../projects/auth/src/lib/services/login.service';
import {AuthValidationService} from '../../../projects/auth/src/lib/services/auth-validation.service';
import {catchError, firstValueFrom, from, of} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SessionStoreDataService} from '../../../projects/products-lib/src/lib/services/session-store-data.service';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

@Component({
  selector: 'app-login-component',
  imports: [ReactiveFormsModule],
  templateUrl: './login-component.html',
  styleUrls: ['./login-component.sass'],
  providers: []
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  submitted = false;
  errorMessage: SafeHtml | null = null;
  isLoading = false;
  showSuccess = signal(false);
  isAlreadyLoggedIn = signal(false);

  private loginService = new LoginService();

  constructor(
    @Inject(ENV_TOKEN)
    protected readonly environment: EnvInterface,
    private readonly sessionStoreDataService: SessionStoreDataService,
    private readonly authValidationService: AuthValidationService,
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  get f() {
    return this.loginForm.controls;
  }

  ngOnInit(): void {
    // Check for logout reason
    const logoutReason = sessionStorage.getItem('logoutReason');
    if (logoutReason) {
      this.errorMessage = this.buildErrorMessage(logoutReason);
      sessionStorage.removeItem('logoutReason');
    }

    // Only check if already logged in when an access token is present in sessionStorage.
    // Do NOT call validateToken() here — that would trigger tryRefresh() via the cookie
    // and silently re-log in the user immediately after they explicitly logged out.
    const existingToken = this.authValidationService.getStoredToken();
    if (existingToken) {
      this.authValidationService.callValidateOnly().subscribe(result => {
        this.isAlreadyLoggedIn.set(result.isValid);
        this.cdRef.detectChanges();
      });
    }
  }

  async onSubmit() {
    this.submitted = true;
    this.errorMessage = null;
    if (this.loginForm.invalid) {
      return;
    }
    this.isLoading = true;
    try {
      const loginResponse = await firstValueFrom(
        from(this.loginService.login(
          this.loginForm.value.username,
          this.loginForm.value.password
        )).pipe(
          //timeout(10000),
          catchError(err => {
            //console.log(err);
            if (err.name === 'UnknownError' || err.message.includes('timeout')) {
              this.errorMessage = this.buildErrorMessage('Login request timed out. Please try again.');
              //console.log('Login request timed out');
              return of(null);
            }
            throw err;
          })
        )
      );

      if (!this.errorMessage && loginResponse) {
        // Extract access token
        const accessToken = loginResponse.accessToken
          ?? loginResponse.token
          ?? loginResponse.data?.accessToken
          ?? loginResponse.data?.token;

        //console.log('Login response: accessToken found:', !!accessToken);

        if (!accessToken) {
          this.errorMessage = this.buildErrorMessage('Login failed: no token received from server.');
          console.error('Token extraction failed. Full response:', JSON.stringify(loginResponse));
          return;
        }

        // Store access token in sessionStorage
        // The HttpOnly refresh cookie is set automatically by the server — Angular never touches it
        this.authValidationService.storeToken(accessToken);
        this.sessionStoreDataService.setStoredData('authToken', accessToken);

        // Show success block
        this.showSuccess.set(true);
        // Wait 2 seconds, then redirect
        setTimeout(() => {
          this.showSuccess.set(false);

          // Check for isPupilMember claim — redirect to 11+ dashboard
          const decodedToken = this.sessionStoreDataService.getDecodedTokenUser();

          if (decodedToken?.isPupilMember) {
            this.router.navigateByUrl('/11-plus-online-tests');
            return;
          }

          if (this.sessionStoreDataService.hasFullAccess()) {
            this.router.navigateByUrl('/product-selector');
            return;
          }

          if (decodedToken?.hasActiveDigitalProducts) {
            this.router.navigateByUrl('/revision-hub');
            return;
          }

          if (decodedToken?.hasActiveSubscription) {
            this.router.navigateByUrl('/11-plus-online-account');
            return;
          }

          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? '/';
          this.router.navigateByUrl(returnUrl);
        }, 1000);
      } else {
        this.errorMessage = this.buildErrorMessage(this.errorMessage ? String(this.errorMessage) : loginResponse.errorMessage || 'The email address or password provided was not recognised - please try again or reset your password.');
      }
    } catch (error: any) {
      this.errorMessage = this.buildErrorMessage(error?.error?.message || 'The email address or password provided was not recognised - please try again or reset your password.');
      //console.log('Login failed. Please try again.');
    } finally {
      await this.delay(3000); // 2 second delay before fetching

      this.isLoading = false;
      this.cdRef.detectChanges();
    }
  }

  private async delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private buildErrorMessage(raw: string): SafeHtml {
    const forgottenPasswordUrl = `${this.environment.app.mediaBaseUrl}/forgotten-password`;
    const withLink = raw.replace(
      'reset your password',
      `<a href="${forgottenPasswordUrl}" target="_blank" rel="noopener noreferrer" style="color: inherit; font-weight: bold; text-decoration: underline;">reset your password</a>`
    );
    return this.sanitizer.bypassSecurityTrustHtml(withLink);
  }
}
